import { Binding } from 'core/infrastructure';
import { FileUtils } from '../../utils';
import { DataGridColumn } from './DataGrid.Column';

export function saveDataToExcel<T>(items: T[], columns: DataGridColumn<T>[], fileNameWithoutExtension: string) {
	const blob = exportDataToExcelBlob<T>(items, columns, fileNameWithoutExtension);
	FileUtils.saveBlobAs(blob, `${fileNameWithoutExtension}.xls`);
}

export function printData<T>(items: T[], columns: DataGridColumn<T>[], preTableHtmlContent?: string, postTableHtmlContent?: string) {
	const html = getExcelContentAsHtml('', items, columns, preTableHtmlContent, postTableHtmlContent);
	const view = window.open('') as any;
	if (view) {
		view.document.write();
		view.document.write(html);
		view.document.write(`
<script>
function waitForImages(callback) {
	var images = Array.prototype.filter.call(document.querySelectorAll("img"), function(img) {
		return img.src && !img.complete;
	});

	var pending = images.length;

	pending === 0 ? callback() : images.forEach(function(img) {
		var image = new Image();
		image.addEventListener("load", function() {
			--pending <= 0 && callback();
		});
		image.src = img.src;
	});
}
waitForImages(function() { print() });
</script>`);
		view.document.close();
	}
}

// Duplicado de admin/Images.ts para no tener una dependencia circular
function dbImage(id: string, size?: 'thumb'): string | undefined {
	const sizeParam = size ? `&size=${size}` : '';
	return id && id !== '00000000-0000-0000-0000-000000000000' ? `/db-image/?id=${id}${sizeParam}` : undefined;
}

export function exportDataToExcelBlob<T>(items: T[], columns: DataGridColumn<T>[], fileNameWithoutExtension: string) {
	const tableContent = getExcelContentAsHtml(fileNameWithoutExtension, items, columns);
	return new Blob([tableContent], { type: 'data:application/vnd.ms-excel' });
}

function getTdContent(cellClassName: string | undefined, value: any, useRelativePath: boolean): string {
	if (value === null)
		return '';

	if (cellClassName && cellClassName.indexOf('image-column') >= 0) {
		const path = dbImage(value, 'thumb');
		if (!path)
			return '';

		const src = useRelativePath ? path : `${window.location.protocol}${window.location.host}${path}`;
		return `<img src='${src}' />`;
	}

	return value;
}

function getExcelContentAsHtml<T>(worksheetName: string, items: T[], columns: DataGridColumn<T>[], preTableHtmlContent?: string, postTableHtmlContent?: string): string {

	const visibleColumns = columns.filter(c => c.isVisible || typeof c.isVisible === 'undefined');

	const header = visibleColumns.map(({ width, title, headerClassName }, idx) =>
		`<th class='header' align=${guessAlignment(headerClassName)} width='${width || 120}' ${idx === 0 ? getAutoFilter(items, visibleColumns) : ''} >${title}</th>`).join('');

	const defaultFormat = (value: any, _item: T) => value;

	const rows = items.map(item => {
		const cells = visibleColumns.map(({ width, linkTo, cellClassName, format = defaultFormat }) => {
			const value = format(Binding.getValue(item, Binding.propertyPathToString(linkTo)), item);
			const tdContent = getTdContent(cellClassName, value, worksheetName === '');
			return `<td width='${width || 120}' align=${guessAlignment(cellClassName)}>${tdContent}</td>`;
		}).join('');
		return `<tr>${cells}</tr>`;
	}).join('');

	const totalRow = '<tr>' + visibleColumns.map((c, idx) => {
		const className = c.showFooterAsHours ? 'footerAsHours' : 'footer';
		return c.totalize
			? `<td class='${className}' align=${guessAlignment(c.cellClassName)} ${getTotalFormula(items, c.totalize, idx)}>` + getTotal(items, c) + '</td>'
			: '<td></td>';
	}).join('') + '</tr>';

	const table = `<table>
			<thead>
				<tr>
					${header}
				</tr>
			</thead>
			<tbody>
				${rows.concat(totalRow)}
			</tbody>
		</table>`;

	return `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:x="urn:schemas-microsoft-com:office:excel" xmlns="http://www.w3.org/TR/REC-html40">
<head>
  <meta charset="UTF-8">
  <style>
    .header {background-color: #ec133d; color: white; font-weight: bold}
    .footer {font-weight: bold}
	.footerAsHours {font-weight: bold; mso-number-format:"[hh]:mm;@"}
	td { vertical-align: top }
    body, td, th {
        background: white;
		font-family: Arial, Helvetica, sans-serif;
		font-size: 12px;
	}
  </style>
  <!--[if gte mso 9]><xml><x:ExcelWorkbook><x:ExcelWorksheets><x:ExcelWorksheet><x:Name>${worksheetName}</x:Name><x:WorksheetOptions><x:DisplayGridlines/></x:WorksheetOptions></x:ExcelWorksheet></x:ExcelWorksheets></x:ExcelWorkbook></xml><![endif]-->
</head>
<body>
  ${preTableHtmlContent || ''}
  ${table}
  ${postTableHtmlContent || ''}
</body>
</html>`;
}

function getTotalFormula<T>(data: T[], totalize: 'sum' | 'count' | 'avg', columnIndex: number) {
	const name = getExcelColumnName(columnIndex);
	const end = data.length + 1;
	const func = { 'sum': 9, 'count': 3, 'avg': 1 }[totalize];
	return `x:fmla='=SUBTOTAL(${func}, ${name}2:${name}${end})'`;
}

export function getTotal<T>(data: T[], { linkTo, format, totalize }: DataGridColumn<T>) {
	let total = 0;
	switch (totalize) {
		case 'sum':
			total = data.sum(x => Binding.getValue(x, Binding.propertyPathToString(linkTo)));
			break;
		case 'count':
			total = data.length;
			break;
		case 'avg':
			total = data.sum(x => Binding.getValue(x, Binding.propertyPathToString(linkTo))) / data.length;
			break;
		default:
			return '';
	}
	// Si hay una función para formatear valores, lo mantengo en el total (así quedará como
	// precio, cantidad o lo que sea). Confío en que ese formato sólo dependa del valor y no del item.
	return format ? format(total, {} as T) : total;
}

function getAutoFilter<T>(data: T[], columns: DataGridColumn<T>[]) {
	const endCol = getExcelColumnName(columns.length);
	const endRow = data.length + 1;
	return `x:autofilterrange='$A$1:$${endCol}$${endRow}'`;
}


function guessAlignment(className = '') {
	// Esto es una ñapa como un castillo, pero parsear el css a través de los document.styleSheets
	// no es trivial, y para nuestro caso de uso nos puede servir esto.
	// Pongo right tanto para center como para right, porque en la aplicación web estamos usando
	// center para cosas que deberían ser right (números o fechas) por cuestiones estéticas

	if (className.match(/right/) || className.match(/center/))
		return 'right';

	return 'left';
}

// Copiado flagrantemente de aquí y adaptado: https://github.com/avilaton/excel-column-name/blob/master/index.js
function getExcelColumnName(index: number) {
	let dividend = Math.floor(index) + 1;
	let columnName = '';
	let modulo;

	while (dividend > 0) {
		modulo = (dividend - 1) % 26;
		columnName = String.fromCharCode(65 + modulo) + columnName;
		dividend = Math.floor((dividend - modulo) / 26);
	}

	return columnName;
}


